import {List, Set} from 'immutable';

import {BillPayer as RestBillPayer, BillPayerTag, PaymentSource} from 'web-app/react/entities/bill-payers/model';
import {Child as RestChild} from 'web-app/react/entities/children/types';
import Name from 'web-app/react/entities/common/name';
import Image from 'web-app/react/entities/common/image';
import {getBehaviorsCollectionFromGraphQL} from 'web-app/behaviors/behaviors-collection';

import {type DetailsData} from './types';

/**
 * These types are modelling of the GQL response returned for the billPayer.byInstitutionSetIds field.
 * We only model the fields that we need for the debt-report.
 *
 * This is temporary until we re-build the debt-report to use the actual GQL return types.
 */
type BillPayerCategory = {
    id: string;
    name: string;
    siteSetId: string;
};

type Payment = {
    id: string;
    amount: string;
    paymentDate: string;
    paymentMethod: string;
    note: string;
};

type OutstandingDebt = {
    latestPayment?: Payment | null;
    overdue: string;
    overdue30: string;
    overdue60: string;
};

type PaymentReminder = {
    paymentReminderEmailId: string;
    subject: string;
    createdAt: string;
};

type BehaviorFromGQL = {
    behaviorId: string;
    sourceType?: string | null;
    active?: boolean | null;
};

type BillPayerGQL = {
    billPayerId: string;
    name: string;
    email?: string | null;
    accountName?: string | null;
    accountNumber?: string | null;
    sortCode?: string | null;
    iban?: string | null;
    bic?: string | null;
    billingReference?: string | null;
    note?: string | null;
    childrenThroughShares: ChildFromGQL[];
    phone?: string | null;
};

type PaymentMethod = {
    type: string;
    active: boolean;
    status: string;
    title: string;
};

export type BillPayerWithBalanceGQL = {
    billPayer: BillPayerGQL;
    balance: string;
    debt?: OutstandingDebt | null;
    categories: BillPayerCategory[];
    latestPaymentReminder?: PaymentReminder | null;
    behaviors: BehaviorFromGQL[];
    availablePaymentsSources: PaymentMethod[];
};

const billPayerDetailsFromGQLBillPayerWithBalance = (billPayerFromGQL: BillPayerWithBalanceGQL): DetailsData => {
    return {
        balance: Number(billPayerFromGQL.balance),
        debt: billPayerFromGQL.debt ? Number(billPayerFromGQL.debt.overdue) : 0.0,
        lastPayment: billPayerFromGQL.debt?.latestPayment
            ? {
                  id: billPayerFromGQL.debt?.latestPayment?.id ?? '',
                  date: billPayerFromGQL.debt?.latestPayment?.paymentDate ?? '',
                  formattedDate: billPayerFromGQL.debt?.latestPayment?.paymentDate ?? '',
                  formattedAmount: billPayerFromGQL.debt?.latestPayment?.amount ?? '',
                  paymentMethod: billPayerFromGQL.debt?.latestPayment?.paymentMethod ?? '',
                  note: billPayerFromGQL.debt?.latestPayment?.note ?? null,
              }
            : null,
        lastPaymentReminderEmail: billPayerFromGQL.latestPaymentReminder
            ? {
                  createdAt: billPayerFromGQL.latestPaymentReminder?.createdAt ?? '',
                  formattedDate: billPayerFromGQL.latestPaymentReminder?.createdAt ?? '',
                  id: billPayerFromGQL.latestPaymentReminder?.paymentReminderEmailId ?? '',
              }
            : null,
        overdue: billPayerFromGQL.debt ? Number(billPayerFromGQL.debt.overdue) : 0.0,
        overdue30d: billPayerFromGQL.debt ? Number(billPayerFromGQL.debt.overdue30) : 0.0,
    };
};

type NameFromGQL = {
    fullName: string;
    firstName: string;
    middleName?: string | null;
    lastName?: string | null;
};
type GroupFromGQL = {
    id: string;
    title: string;
};
type ProfileImageFromGQL = {
    url: string;
};
type ChildFromGQL = {
    id: string;
    institutionId: string;
    name: NameFromGQL;
    profileImage?: ProfileImageFromGQL | null;
    endDate?: string | null;
    group?: GroupFromGQL | null;
};

const billPayerFromGQLBillPayerWithBalance = (billPayerFromGQL: BillPayerWithBalanceGQL): RestBillPayer => {
    return new RestBillPayer({
        billPayerId: billPayerFromGQL.billPayer.billPayerId,
        note: billPayerFromGQL.billPayer.note ?? '',
        name: billPayerFromGQL.billPayer.name,
        email: billPayerFromGQL.billPayer.email ?? undefined,
        phone: billPayerFromGQL.billPayer.phone ?? undefined,
        accountName: billPayerFromGQL.billPayer.accountName ?? undefined,
        accountNo: billPayerFromGQL.billPayer.accountNumber ?? undefined,
        sortCode: billPayerFromGQL.billPayer.sortCode ?? undefined,
        billingReference: billPayerFromGQL.billPayer.billingReference ?? undefined,
        IBAN: billPayerFromGQL.billPayer.iban ?? undefined,
        BIC: billPayerFromGQL.billPayer.bic ?? undefined,
        deleted: false,
        shares: List(
            billPayerFromGQL.billPayer.childrenThroughShares.map(childFromGQL => {
                return {
                    billPayerId: billPayerFromGQL.billPayer.billPayerId,
                    childId: childFromGQL.id,
                    share: 0, // We do not have the share, but that is ok, we do not need it in this case.
                };
            }),
        ),
        balance: Number(billPayerFromGQL.balance),
        behaviors: getBehaviorsCollectionFromGraphQL(billPayerFromGQL.behaviors),
        tags: Set(billPayerFromGQL.categories.map(cat => new BillPayerTag({id: cat.id, name: cat.name}))),
        paymentSources: Set(billPayerFromGQL.availablePaymentsSources.map(source => new PaymentSource(source))),
    });
};

export const convertChildFromGQLToRest = (childFromGQL: ChildFromGQL): RestChild => {
    return new RestChild({
        id: childFromGQL.id,
        childId: childFromGQL.id,
        name: Name.fromAPI({
            fullName: childFromGQL.name.fullName,
            firstName: childFromGQL.name.firstName,
            lastName: childFromGQL.name.lastName,
            middleName: childFromGQL.name.middleName,
            salutation: '',
        }),
        image: Image.fromAPI(childFromGQL.profileImage?.url ?? 'img/empty-states/profile-image.svg'),
        endDate: childFromGQL.endDate ?? null,
        groupId: childFromGQL.group?.id,
        groupTitle: childFromGQL.group?.title,
        institutionId: childFromGQL.institutionId,
    });
};

export const convertFromGQLBillPayerToRESTBillPayer = (billPayers: BillPayerWithBalanceGQL[]): List<RestBillPayer> => {
    return List(billPayers.map(billPayerFromGQL => billPayerFromGQLBillPayerWithBalance(billPayerFromGQL)));
};

export const convertFromGQLBillPayerToRESTBillPayerWithDetails = (
    billPayers: BillPayerWithBalanceGQL[],
): List<{billPayer: RestBillPayer; details: DetailsData}> => {
    return List(
        billPayers.map(billPayerFromGQL => ({
            billPayer: billPayerFromGQLBillPayerWithBalance(billPayerFromGQL),
            details: billPayerDetailsFromGQLBillPayerWithBalance(billPayerFromGQL),
        })),
    );
};
